import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@project/contexts/AuthContext";
import { motion } from "framer-motion";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  Users,
  UserCheck,
  User,
} from "lucide-react";
import { toast } from "@project/hooks/use-toast";
import hireflowLogo from "@project/assets/hireflow-logo.png";
import atsBg from "@project/assets/ats-bg.jpg";
import navigationRoutes from "@utils/navigationRoutes";

const demoAccounts = [
  {
    email: "admin@hireflow.com",
    role: "Admin",
    icon: ShieldCheck,
    color: "from-primary to-primary-glow",
  },
  {
    email: "hr@hireflow.com",
    role: "HR",
    icon: Users,
    color: "from-[hsl(160_84%_39%)] to-[hsl(160_70%_50%)]",
  },
  {
    email: "interviewer@hireflow.com",
    role: "Interviewer",
    icon: UserCheck,
    color: "from-[hsl(38_92%_50%)] to-[hsl(38_80%_60%)]",
  },
  {
    email: "candidate@hireflow.com",
    role: "Candidate",
    icon: User,
    color: "from-[hsl(199_89%_48%)] to-[hsl(199_70%_58%)]",
  },
];

export default function Login() {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPw, setSignupConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (loginEmail?: string) => {
    const e = loginEmail || email;
    if (!e) {
      toast({ title: "Enter email", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await login(e, password);
      navigate(navigationRoutes.ATS_DASHBOARD);
      toast({ title: "Welcome!", description: `Logged in as ${e}` });
    } catch (err: any) {
      toast({
        title: "Login Failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!signupName.trim()) {
      toast({ title: "Enter your name", variant: "destructive" });
      return;
    }
    if (!signupEmail.trim()) {
      toast({ title: "Enter your email", variant: "destructive" });
      return;
    }
    if (signupPassword.length < 6) {
      toast({
        title: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }
    if (signupPassword !== signupConfirmPw) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      await signup(signupName, signupEmail, signupPassword);
      toast({
        title: "Account Created!",
        description: "Please complete your profile.",
      });
      navigate(navigationRoutes.ATS_PROFILE_SETUP);
    } catch (err: any) {
      toast({
        title: "Signup Failed",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center p-4">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <img src={atsBg} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="section-card space-y-6 p-6 sm:p-8">
          {/* Logo */}
          <div className="space-y-2 text-center">
            <div className="mb-2 flex items-center justify-center gap-2.5">
              <img
                src={hireflowLogo}
                alt="HireFlow"
                className="h-10 w-auto rounded-xl object-contain"
              />
              <div className="text-left">
                <h1 className="font-display text-xl font-bold text-foreground">
                  HireFlow
                </h1>
                <p className="text-[10px] text-muted-foreground">
                  AI-Powered ATS
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 rounded-xl bg-muted/50 p-1">
            <button
              onClick={() => setMode("login")}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition-all ${mode === "login" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition-all ${mode === "signup" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              New Candidate
            </button>
          </div>

          {mode === "login" ? (
            <>
              {/* Login Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="h-10 pl-10"
                      onKeyDown={e => e.key === "Enter" && handleLogin()}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="h-10 pl-10 pr-10"
                      onKeyDown={e => e.key === "Enter" && handleLogin()}
                    />
                    <button
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button
                  onClick={() => handleLogin()}
                  disabled={loading}
                  className="btn-glow h-10 w-full font-semibold"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
              </div>

              {/* Demo Accounts */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    Quick Login
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {demoAccounts.map(acc => (
                    <button
                      key={acc.email}
                      onClick={() => handleLogin(acc.email)}
                      className="flex items-center gap-2 rounded-xl border border-border/60 bg-card/50 p-2.5 text-left transition-all hover:border-border hover:bg-card hover:shadow-sm"
                    >
                      <div
                        className={`h-8 w-8 rounded-lg bg-gradient-to-br ${acc.color} flex shrink-0 items-center justify-center`}
                      >
                        <acc.icon className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold text-foreground">
                          {acc.role}
                        </p>
                        <p className="truncate text-[9px] text-muted-foreground">
                          {acc.email}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Signup Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Your full name"
                      value={signupName}
                      onChange={e => setSignupName(e.target.value)}
                      className="h-10 pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={signupEmail}
                      onChange={e => setSignupEmail(e.target.value)}
                      className="h-10 pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">
                    Password <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="Min 6 characters"
                      value={signupPassword}
                      onChange={e => setSignupPassword(e.target.value)}
                      className="h-10 pl-10 pr-10"
                    />
                    <button
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-medium">
                    Confirm Password <span className="text-destructive">*</span>
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder="Re-enter password"
                      value={signupConfirmPw}
                      onChange={e => setSignupConfirmPw(e.target.value)}
                      className="h-10 pl-10"
                      onKeyDown={e => e.key === "Enter" && handleSignup()}
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSignup}
                  disabled={loading}
                  className="btn-glow h-10 w-full font-semibold"
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </Button>
              </div>
              <p className="text-center text-[11px] text-muted-foreground">
                By signing up you agree to our Terms & Privacy Policy
              </p>
            </>
          )}
        </div>

        <p className="mt-4 text-center text-[11px] text-muted-foreground/60">
          © 2026 HireFlow · AI-Powered Applicant Tracking System
        </p>
      </motion.div>
    </div>
  );
}
